export const getDaysInMonthGrid = (currentDate: Date) => {
	const year = currentDate.getFullYear();
	const month = currentDate.getMonth();

	const firstDayOfMonth = new Date(year, month, 1);
	const lastDayOfMonth = new Date(year, month + 1, 0);

	const prevDaysCount = firstDayOfMonth.getDay();
	const prevMonthLastDay = new Date(year, month, 0).getDate();

	const grid = [];

	for (let i = prevDaysCount - 1; i >= 0; i--) {
		grid.push({
			date: new Date(year, month - 1, prevMonthLastDay - i),
			isCurrentMonth: false,
		});
	}

	for (let i = 1; i <= lastDayOfMonth.getDate(); i++) {
		grid.push({
			date: new Date(year, month, i),
			isCurrentMonth: true,
		});
	}

	const remaining = (7 - (grid.length % 7)) % 7;
	for (let i = 1; i <= remaining; i++) {
		grid.push({
			date: new Date(year, month + 1, i),
			isCurrentMonth: false,
		});
	}

	return grid;
};

export const getDaysInWeekGrid = (currentDate: Date) => {
	const date = new Date(currentDate);
	const day = date.getDay(); // 0 (Domingo)
	const diff = date.getDate() - day;
	const sunday = new Date(date.setDate(diff));

	const week = [];
	for (let i = 0; i < 7; i++) {
		const d = new Date(sunday);
		d.setDate(sunday.getDate() + i);
		week.push(d);
	}
	return week;
};

export const formatLocalDate = (date: Date) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

export const parseLocalDateYmd = (ymd: string): Date => {
	const [year, month, day] = ymd.split('-').map(Number);
	return new Date(year, month - 1, day);
};

export const filterSessionsByHistoryDateRange = (
	sessions: Sessions.Session[],
	range: { start: string | null; end: string | null },
): Sessions.Session[] => {
	const { start, end } = range;
	if (!start && !end) return sessions;

	return sessions.filter((session) => {
		const sessionDate = new Date(session.selected_date);
		if (start) {
			const rangeStart = startOfLocalDay(parseLocalDateYmd(start));
			if (sessionDate < rangeStart) return false;
		}
		if (end) {
			const rangeEnd = endOfLocalDay(parseLocalDateYmd(end));
			if (sessionDate > rangeEnd) return false;
		}
		return true;
	});
};

export const startOfLocalDay = (date: Date): Date =>
	new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);

export const endOfLocalDay = (date: Date): Date =>
	new Date(
		date.getFullYear(),
		date.getMonth(),
		date.getDate(),
		23,
		59,
		59,
		999,
	);

/** Limites do mês civil de `date` (início/fim do dia, horário local). */
export const getMonthBoundsLocal = (date: Date): { start: Date; end: Date } => {
	const year = date.getFullYear();
	const month = date.getMonth();

	return {
		start: new Date(year, month, 1, 0, 0, 0, 0),
		end: new Date(year, month + 1, 0, 23, 59, 59, 999),
	};
};

type ProfessionalCalendarViewMode = 'MONTH' | 'WEEK';

/**
 * Intervalo para GET /session/me na home do profissional.
 * - Mês: cobre todo o grid visível (inclui dias cinza dos meses adjacentes).
 * - Semana: cobre pelo menos o mês civil de `currentDate` (fim do último dia).
 */
export const getSessionTimeSlotKey = (session: Sessions.Session): string => {
	const date = new Date(session.selected_date);
	return `${date.getHours()}:${date.getMinutes()}`;
};

export const CALENDAR_SESSION_DURATION_MINUTES = 50;
export const CALENDAR_WEEK_ACTIVE_SESSION_HEIGHT_RATIO = 50 / 60;
/** Faixa compacta do cancelado na semana (somente quando não há consulta ativa no horário). */
export const CALENDAR_WEEK_CANCELLED_CARD_HEIGHT = 18;
export const CALENDAR_WEEK_OVERFLOW_CHIP_HEIGHT = 14;
export const CALENDAR_MONTH_SESSION_ROW_HEIGHT = 22;

export type CalendarDayDisplayItem = {
	kind: 'session';
	session: Sessions.Session;
};

export const formatSessionTimeRange = (selectedDate: string): string => {
	const startTime = new Date(selectedDate);
	const endTime = new Date(
		startTime.getTime() + CALENDAR_SESSION_DURATION_MINUTES * 60 * 1000,
	);

	const formatTime = (date: Date) =>
		date.toLocaleTimeString('pt-BR', {
			hour: '2-digit',
			minute: '2-digit',
			hour12: false,
		});

	return `${formatTime(startTime)} - ${formatTime(endTime)}`;
};

export const isCancelledSession = (session: Sessions.Session): boolean =>
	session.status === 'CANCELLED';

export const groupSessionsByTimeSlot = (
	sessions: Sessions.Session[],
): Map<string, Sessions.Session[]> => {
	const bySlot = new Map<string, Sessions.Session[]>();

	for (const session of sessions) {
		const key = getSessionTimeSlotKey(session);
		const group = bySlot.get(key) ?? [];
		group.push(session);
		bySlot.set(key, group);
	}

	return bySlot;
};

/** 0 = em andamento, 1 = a acontecer, 2 = já passou. */
export const getSessionDayDisplaySortTier = (
	session: Sessions.Session,
	now: Date = new Date(),
): 0 | 1 | 2 => {
	if (session.status === 'STARTED') return 0;

	const startMs = new Date(session.selected_date).getTime();
	const endMs = startMs + CALENDAR_SESSION_DURATION_MINUTES * 60 * 1000;
	const nowMs = now.getTime();

	if (session.status === 'FINISHED' || session.status === 'CANCELLED') {
		return 2;
	}

	if (nowMs >= endMs) return 2;

	if (startMs > nowMs) return 1;

	return 1;
};

/** Em andamento primeiro, depois futuras, por último as que já passaram. */
export const sortSessionsForDayDisplay = (
	sessions: Sessions.Session[],
	now: Date = new Date(),
): Sessions.Session[] =>
	[...sessions].sort((a, b) => {
		const tierDiff =
			getSessionDayDisplaySortTier(a, now) -
			getSessionDayDisplaySortTier(b, now);
		if (tierDiff !== 0) return tierDiff;

		return (
			new Date(a.selected_date).getTime() -
			new Date(b.selected_date).getTime()
		);
	});

/** Ordena por horário; no mesmo slot, canceladas aparecem antes das válidas. */
export const sortSessionsForCalendarDisplay = (
	sessions: Sessions.Session[],
): Sessions.Session[] =>
	[...sessions].sort((a, b) => {
		const timeDiff =
			new Date(a.selected_date).getTime() -
			new Date(b.selected_date).getTime();
		if (timeDiff !== 0) return timeDiff;

		const aCancelled = isCancelledSession(a);
		const bCancelled = isCancelledSession(b);
		if (aCancelled && !bCancelled) return -1;
		if (!aCancelled && bCancelled) return 1;
		return 0;
	});

export type CalendarSlotSessionSplit = {
	primarySession: Sessions.Session | null;
	overflowSessions: Sessions.Session[];
};

/** Uma consulta em destaque por horário; o restante (válidas extras + canceladas) vai para +N. */
export const splitCalendarSlotSessions = (
	slotSessions: Sessions.Session[],
): CalendarSlotSessionSplit => {
	const sorted = sortSessionsForCalendarDisplay(slotSessions);
	const active = sorted.filter((session) => !isCancelledSession(session));
	const cancelled = sorted.filter(isCancelledSession);

	if (active.length > 0) {
		return {
			primarySession: active[0],
			overflowSessions: [...active.slice(1), ...cancelled],
		};
	}

	if (cancelled.length <= 1) {
		return {
			primarySession: cancelled[0] ?? null,
			overflowSessions: [],
		};
	}

	return {
		primarySession: null,
		overflowSessions: cancelled,
	};
};

export const countCalendarSlotOverflowSessions = (
	sessions: Sessions.Session[],
): number => {
	let total = 0;

	for (const group of groupSessionsByTimeSlot(sessions).values()) {
		total += splitCalendarSlotSessions(group).overflowSessions.length;
	}

	return total;
};

/** Uma consulta visível por horário (a primeira válida, ou a única cancelada). */
export const pickVisibleSessionsForCalendar = (
	sessions: Sessions.Session[],
): Sessions.Session[] => {
	const visible: Sessions.Session[] = [];

	for (const group of groupSessionsByTimeSlot(sessions).values()) {
		const { primarySession } = splitCalendarSlotSessions(group);
		if (primarySession) {
			visible.push(primarySession);
		}
	}

	return sortSessionsForCalendarDisplay(visible);
};

export const buildCalendarDayDisplayItems = (
	sessions: Sessions.Session[],
): CalendarDayDisplayItem[] =>
	sortSessionsForCalendarDisplay(sessions).map((session) => ({
		kind: 'session',
		session,
	}));

export const getCalendarMonthDisplayItemHeight = (): number =>
	CALENDAR_MONTH_SESSION_ROW_HEIGHT;

export const dayHasActiveSessionAtHour = (
	daySessions: Sessions.Session[],
	hour: number,
): boolean =>
	daySessions.some(
		(session) =>
			!isCancelledSession(session) &&
			new Date(session.selected_date).getHours() === hour,
	);

export const getWeekBoundsLocal = (date: Date): { start: Date; end: Date } => {
	const week = getDaysInWeekGrid(date);
	return {
		start: startOfLocalDay(week[0]),
		end: endOfLocalDay(week[6]),
	};
};

export const countVisibleSessionsOnDay = (
	sessions: Sessions.Session[],
	day: Date,
): number => {
	const ymd = formatLocalDate(day);
	const daySessions = sessions.filter(
		(session) => formatLocalDate(new Date(session.selected_date)) === ymd,
	);
	return pickVisibleSessionsForCalendar(daySessions).length;
};

export const countVisibleSessionsInWeek = (
	sessions: Sessions.Session[],
	anchorDate: Date = new Date(),
): number =>
	getDaysInWeekGrid(anchorDate).reduce(
		(sum, day) => sum + countVisibleSessionsOnDay(sessions, day),
		0,
	);

const UPCOMING_SESSION_STATUSES: Sessions.SessionStatus[] = [
	'CREATED',
	'CONFIRMED',
	'STARTED',
];

export const isUpcomingSession = (
	session: Sessions.Session,
	now: Date = new Date(),
): boolean => {
	if (isCancelledSession(session)) return false;
	if (!UPCOMING_SESSION_STATUSES.includes(session.status)) return false;
	return new Date(session.selected_date).getTime() >= now.getTime();
};

export const countRemainingVisibleSessionsOnDay = (
	sessions: Sessions.Session[],
	day: Date = new Date(),
	now: Date = new Date(),
): number => {
	const ymd = formatLocalDate(day);
	const daySessions = sessions.filter((session) => {
		if (formatLocalDate(new Date(session.selected_date)) !== ymd)
			return false;
		return isUpcomingSession(session, now);
	});
	return pickVisibleSessionsForCalendar(daySessions).length;
};

export const countUpcomingVisibleSessions = (
	sessions: Sessions.Session[],
	now: Date = new Date(),
): number => {
	const byDay = new Map<string, Sessions.Session[]>();

	for (const session of sessions) {
		if (!isUpcomingSession(session, now)) continue;
		const ymd = formatLocalDate(new Date(session.selected_date));
		const daySessions = byDay.get(ymd) ?? [];
		daySessions.push(session);
		byDay.set(ymd, daySessions);
	}

	let total = 0;
	for (const daySessions of byDay.values()) {
		total += pickVisibleSessionsForCalendar(daySessions).length;
	}

	return total;
};

export const countRemainingVisibleSessionsInWeek = (
	sessions: Sessions.Session[],
	now: Date = new Date(),
): number => {
	const weekDays = getDaysInWeekGrid(now);
	const weekEnd = endOfLocalDay(weekDays[6]);
	const byDay = new Map<string, Sessions.Session[]>();

	for (const session of sessions) {
		if (!isUpcomingSession(session, now)) continue;

		const sessionDate = new Date(session.selected_date);
		if (sessionDate.getTime() > weekEnd.getTime()) continue;

		const ymd = formatLocalDate(sessionDate);
		const daySessions = byDay.get(ymd) ?? [];
		daySessions.push(session);
		byDay.set(ymd, daySessions);
	}

	let total = 0;
	for (const daySessions of byDay.values()) {
		total += pickVisibleSessionsForCalendar(daySessions).length;
	}

	return total;
};

/** Limite de navegação da home: fim do próximo mês civil. */
export const getProfessionalHomeMaxLimitDate = (
	anchorDate: Date = new Date(),
) => new Date(anchorDate.getFullYear(), anchorDate.getMonth() + 2, 0);

export const getProfessionalHomeSummarySessionsDateRange = (
	anchorDate: Date = new Date(),
): { dateA: Date; dateB: Date } => {
	const weekBounds = getWeekBoundsLocal(anchorDate);
	const maxLimitDate = getProfessionalHomeMaxLimitDate(anchorDate);

	return {
		dateA: weekBounds.start,
		dateB: endOfLocalDay(maxLimitDate),
	};
};

export const getProfessionalHomeSessionsDateRange = (
	currentDate: Date,
	view: ProfessionalCalendarViewMode,
): { dateA: Date; dateB: Date } => {
	if (view === 'MONTH') {
		const grid = getDaysInMonthGrid(currentDate);
		const firstDay = grid[0]?.date;
		const lastDay = grid[grid.length - 1]?.date;

		if (!firstDay || !lastDay) {
			const { start, end } = getMonthBoundsLocal(currentDate);
			return { dateA: start, dateB: end };
		}

		return {
			dateA: startOfLocalDay(firstDay),
			dateB: endOfLocalDay(lastDay),
		};
	}

	const weekDays = getDaysInWeekGrid(currentDate);
	const firstDay = weekDays[0];
	const lastDay = weekDays[weekDays.length - 1];

	if (!firstDay || !lastDay) {
		const { start, end } = getMonthBoundsLocal(currentDate);
		return { dateA: start, dateB: end };
	}

	return {
		dateA: startOfLocalDay(firstDay),
		dateB: endOfLocalDay(lastDay),
	};
};

/** Calendário + resumo do rodapé em uma única busca (evita polling duplicado). */
export const getProfessionalHomeCombinedSessionsDateRange = (
	currentDate: Date,
	view: ProfessionalCalendarViewMode,
	anchorDate: Date = new Date(),
): { dateA: Date; dateB: Date } => {
	const calendarRange = getProfessionalHomeSessionsDateRange(
		currentDate,
		view,
	);
	const summaryRange =
		getProfessionalHomeSummarySessionsDateRange(anchorDate);

	return {
		dateA: new Date(
			Math.min(
				calendarRange.dateA.getTime(),
				summaryRange.dateA.getTime(),
			),
		),
		dateB: new Date(
			Math.max(
				calendarRange.dateB.getTime(),
				summaryRange.dateB.getTime(),
			),
		),
	};
};

export const getProfessionalCalendarTimezoneDisplay = (
	date: Date = new Date(),
): { abbr: string; offsetLabel: string } => {
	const shortName =
		new Intl.DateTimeFormat('pt-BR', {
			timeZoneName: 'short',
		})
			.formatToParts(date)
			.find((part) => part.type === 'timeZoneName')?.value ?? 'UTC';

	const offsetHours = -date.getTimezoneOffset() / 60;
	const offsetLabel =
		offsetHours === 0
			? '0'
			: offsetHours > 0
			  ? `+${offsetHours}`
			  : String(offsetHours);

	const abbr =
		shortName.replace(/[+-]?\d+(?::\d+)?$/, '').trim() || shortName;

	return { abbr, offsetLabel };
};
